'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { ELEVAR_EVENT_ORDER } from './elevar-constants';
import {
  markEventAsFired,
  shouldFireEvent,
} from './elevar-event-deduplication';
import { useElevarEvents } from './elevar-events';
import type { ElevarViewProduct } from './elevar-types';

interface ProductViewTrackingProps {
  product: ElevarViewProduct;
  list: string;
}

/**
 * Component to track Elevar dl_view_item events on Product Detail Pages
 * Should be used once per PDP, typically at the page level
 */
export function ProductViewTracking({
  product,
  list,
}: ProductViewTrackingProps) {
  const { trackViewItem } = useElevarEvents();
  const pathname = usePathname();
  const hasFiredRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally exclude trackViewItem to prevent double firing
  useEffect(() => {
    // Skip if already fired in this component instance
    if (hasFiredRef.current) {
      return;
    }

    // Check if this event should fire (handles deduplication)
    if (
      !shouldFireEvent('dl_view_item', pathname, { isInitialPageLoad: true })
    ) {
      return;
    }

    // Add delay to ensure dl_user_data fires first on page load
    const timeoutId = setTimeout(() => {
      // Double-check before firing in case of race conditions
      if (
        !hasFiredRef.current &&
        shouldFireEvent('dl_view_item', pathname, { isInitialPageLoad: true })
      ) {
        trackViewItem({
          products: [product],
          list,
        });

        // Mark as fired to prevent duplicates
        markEventAsFired('dl_view_item', pathname, { isInitialPageLoad: true });
        hasFiredRef.current = true;
      }
    }, ELEVAR_EVENT_ORDER.USER_DATA_DELAY); // Ensure dl_user_data fires first

    return () => clearTimeout(timeoutId);
  }, [pathname]);

  return null;
}
